import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { api } from '../../services/apiClient';
import { theme } from '../../styles/theme';
import Container from '../ui/Container';

/**
 * Wraps footer page content: fetches CMS content by slug. If present, renders it;
 * otherwise renders the default (static) children.
 */
const FooterPageContent = ({ slug, children }) => {
  const [cms, setCms] = useState({ title: null, content: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.footerPages
      .getBySlug(slug)
      .then((data) => {
        if (!cancelled && data?.content) {
          setCms({ title: data.title || null, content: data.content });
        } else {
          setCms({ title: null, content: null });
        }
      })
      .catch((err) => {
        if (!cancelled && err?.response?.status !== 404) {
          console.error('Footer page fetch error:', err);
        }
        setCms({ title: null, content: null });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  const pageStyles = {
    padding: `${theme.spacing.xxl} 0`,
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
  };

  const contentStyles = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: `0 ${theme.spacing.lg}`,
  };

  const headingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xxl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  };

  const bodyStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
  };

  if (loading) {
    return (
      <div style={pageStyles}>
        <Container>
          <div style={contentStyles}>
            <p style={{ color: theme.colors.textSecondary }}>Loading...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (cms.content) {
    // Sanitize HTML content to prevent XSS attacks
    const sanitizedContent = DOMPurify.sanitize(cms.content, {
      ALLOWED_TAGS: [
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'p', 'br', 'hr',
        'strong', 'b', 'em', 'i', 'u', 's', 'strike',
        'ul', 'ol', 'li',
        'a', 'img',
        'blockquote', 'code', 'pre',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'div', 'span'
      ],
      ALLOWED_ATTR: [
        'href', 'target', 'rel',
        'src', 'alt', 'width', 'height',
        'style', 'class',
        'align'
      ],
      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    });

    return (
      <div style={pageStyles}>
        <Container>
          <div style={contentStyles}>
            {cms.title && <h1 style={headingStyles}>{cms.title}</h1>}
            <div
              style={bodyStyles}
              className="footer-page-cms-content"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          </div>
        </Container>
        <style>{`
          .footer-page-cms-content h1 {
            font-size: 2rem;
            font-weight: 700;
            color: ${theme.colors.primaryDark};
            margin: 1.5rem 0 0.75rem;
          }
          .footer-page-cms-content h2 {
            font-size: 1.5rem;
            font-weight: 700;
            color: #CC5500;
            margin: 1.25rem 0 0.5rem;
          }
          .footer-page-cms-content h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: ${theme.colors.primary};
            margin: 1rem 0 0.5rem;
          }
          .footer-page-cms-content h4 {
            font-size: 1.125rem;
            font-weight: 600;
            color: ${theme.colors.primary};
            margin: 1rem 0 0.5rem;
          }
          .footer-page-cms-content p {
            margin-bottom: 0.75rem;
          }
          .footer-page-cms-content ul,
          .footer-page-cms-content ol {
            margin: 0.5rem 0 0.75rem 1.5rem;
            padding-left: 1rem;
          }
          .footer-page-cms-content li {
            margin-bottom: 0.25rem;
          }
          .footer-page-cms-content a {
            color: ${theme.colors.primary};
            text-decoration: underline;
            cursor: pointer;
          }
          .footer-page-cms-content a:hover {
            color: ${theme.colors.primaryDark};
          }
          .footer-page-cms-content blockquote {
            margin: 1rem 0;
            padding: 0.75rem 1rem;
            border-left: 4px solid ${theme.colors.primary};
            background-color: ${theme.colors.surfaceAlt};
            font-style: italic;
            color: ${theme.colors.textSecondary};
          }
          .footer-page-cms-content code {
            background-color: ${theme.colors.surfaceAlt};
            padding: 2px 6px;
            border-radius: ${theme.borderRadius.sm}px;
            font-family: 'Courier New', monospace;
            font-size: 0.9em;
          }
          .footer-page-cms-content strong,
          .footer-page-cms-content b {
            font-weight: 700;
          }
          .footer-page-cms-content em,
          .footer-page-cms-content i {
            font-style: italic;
          }
          .footer-page-cms-content u {
            text-decoration: underline;
          }
          .footer-page-cms-content s,
          .footer-page-cms-content strike {
            text-decoration: line-through;
          }
          .footer-page-cms-content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 1rem 0;
          }
          .footer-page-cms-content table {
            border-collapse: collapse;
            width: 100%;
            margin: 1rem 0;
          }
          .footer-page-cms-content table td,
          .footer-page-cms-content table th {
            border: 1px solid ${theme.colors.border};
            padding: 0.5rem;
            text-align: left;
          }
          .footer-page-cms-content table th {
            background-color: ${theme.colors.surfaceAlt};
            font-weight: 600;
          }
          .footer-page-cms-content hr {
            border: none;
            border-top: 2px solid ${theme.colors.border};
            margin: 1.5rem 0;
          }
          .footer-page-cms-content pre {
            background-color: ${theme.colors.surfaceAlt};
            padding: 1rem;
            border-radius: ${theme.borderRadius.md}px;
            overflow-x: auto;
            margin: 1rem 0;
          }
          .footer-page-cms-content pre code {
            background-color: transparent;
            padding: 0;
          }
        `}</style>
      </div>
    );
  }

  return children;
};

export default FooterPageContent;
